import React, { useState } from 'react';
import { LanguageProvider, useLanguage } from './i18n/LanguageContext';
import LanguageSwitcher from './components/LanguageSwitcher';
import './App.css';

const Calculator = () => {
  const { t } = useLanguage();
  
  // State for form inputs
  const [flowRate, setFlowRate] = useState(100.0);
  const [flowUnit, setFlowUnit] = useState('GPM');
  const [diameter, setDiameter] = useState(50.0);
  const [diameterUnit, setDiameterUnit] = useState('mm');
  const [length, setLength] = useState(100.0);
  const [lengthUnit, setLengthUnit] = useState('meters');
  const [material, setMaterial] = useState('New Steel/Iron (C=150)');
  
  // Elevation and Pressure state
  const [suctionElevation, setSuctionElevation] = useState(0);
  const [dischargeElevation, setDischargeElevation] = useState(10);
  const [elevationUnit, setElevationUnit] = useState('meters');
  const [suctionPressure, setSuctionPressure] = useState(0);
  const [dischargePressure, setDischargePressure] = useState(0);
  const [pressureUnit, setPressureUnit] = useState('bar');
  
  // Fittings state
  const [fittings, setFittings] = useState({
    elbows90: 0,
    elbows45: 0,
    teeThrough: 0,
    teeBranch: 0,
    gateValve: 0,
    globeValve: 0,
    checkValve: 0
  });

  // Results state
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  // Unit conversion factors
  const flowUnitFactors = {
    "m³/s": 1,
    "m³/hr": 1 / 3600,
    "GPM": 0.0000630902,
    "LPM": 0.0000166667
  };

  const diameterUnitFactors = {
    "mm": 0.001,
    "inch": 0.0254
  };

  const lengthUnitFactors = {
    "meters": 1,
    "millimeters": 0.001,
    "feet": 0.3048
  };

  const pressureUnitFactors = {
    "bar": 10.1972,      // bar to meters of water
    "psi": 0.703069,     // psi to meters of water
    "kPa": 0.101972,     // kPa to meters of water
    "mH2O": 1            // meters of water
  };

  // Material options with C-factors
  const materialOptions = {
    "New Steel/Iron (C=150)": 150,
    "New PVC/Plastic (C=140)": 140,
    "HDPE - New (C=150)": 150,
    "HDPE - Used (C=140)": 140,
    "Galvanized Iron - New (C=120)": 120,
    "Galvanized Iron - Used (C=100)": 100,
    "Ductile Iron - New (C=130)": 130,
    "Ductile Iron - Used (C=120)": 120,
    "Cast Iron - New (C=130)": 130,
    "Cast Iron - 10 years (C=110)": 110,
    "Cast Iron - 20 years (C=100)": 100,
    "Cast Iron - 30 years (C=90)": 90,
    "Cast Iron - 40 years (C=80)": 80,
    "Fire Hose - New (C=140)": 140,
    "Fire Hose - Used (C=130)": 130,
    "Garden Hose - Rubber (C=100)": 100,
    "Industrial Hose - New (C=130)": 130
  };

  // K-values for fittings
  const fittingK = {
    "elbows90": 0.9,
    "elbows45": 0.4,
    "teeThrough": 0.3,
    "teeBranch": 1.0,
    "gateValve": 0.2,
    "globeValve": 10,
    "checkValve": 2
  };

  const handleFittingChange = (fittingType, value) => {
    setFittings(prev => ({
      ...prev,
      [fittingType]: parseInt(value) || 0
    }));
  };

  const formatNumber = (num) => {
    if (num >= 0.01) {
      return num.toFixed(3);
    } else {
      return num.toExponential(3);
    }
  };

  const formatVelocity = (num) => {
    if (num >= 0.01) {
      return num.toFixed(2);
    } else {
      return num.toExponential(2);
    }
  };

  const calculateHeadLoss = () => {
    setError('');
    setResults(null);

    const Q = flowRate * flowUnitFactors[flowUnit];
    const D = diameter * diameterUnitFactors[diameterUnit];
    const L = length * lengthUnitFactors[lengthUnit];
    const cFactor = materialOptions[material];

    if (Q <= 0 || D <= 0) {
      setError(t.errorMessage);
      return;
    }

    // Calculate pipe area and velocity
    const area = Math.PI * Math.pow(D, 2) / 4;
    const velocity = Q / area;

    // Calculate equivalent length from fittings
    const totalK = Object.keys(fittings).reduce((sum, key) => {
      return sum + fittings[key] * fittingK[key];
    }, 0);
    
    const equivLength = (totalK * D) / 0.02;
    const totalLength = L + equivLength;

    // Calculate friction head loss using Hazen-Williams equation
    const frictionHead = 10.67 * totalLength * Math.pow(Q / cFactor, 1.85) / Math.pow(D, 4.87);

    // Calculate static head (elevation difference)
    const suctionElev = suctionElevation * lengthUnitFactors[elevationUnit];
    const dischargeElev = dischargeElevation * lengthUnitFactors[elevationUnit];
    const staticHead = dischargeElev - suctionElev;

    // Calculate pressure head
    const suctionPressHead = suctionPressure * pressureUnitFactors[pressureUnit];
    const dischargePressHead = dischargePressure * pressureUnitFactors[pressureUnit];
    const pressureHead = dischargePressHead - suctionPressHead;

    // Calculate velocity head (assuming same diameter, velocity head difference is negligible)
    const velocityHead = Math.pow(velocity, 2) / (2 * 9.81);

    // Calculate Total Dynamic Head (TDH)
    const totalDynamicHead = staticHead + frictionHead + pressureHead + velocityHead;

    setResults({
      velocity,
      staticHead,
      frictionHead,
      pressureHead,
      velocityHead,
      totalDynamicHead,
      equivLength
    });
  };

  return (
    <div className="app">
      <div className="container">
        {/* Language Switcher */}
        <LanguageSwitcher />

        {/* Header */}
        <div className="header">
          <div className="logo-section">
            <img 
              src="https://www.hungpump.com/images/340357" 
              alt="Hung Pump Logo" 
              className="logo"
            />
            <h3 className="company-name">{t.companyName}</h3>
          </div>
        </div>

        <h1 className="main-title">{t.title}</h1>
        
        <p className="description">{t.description}</p>

        {/* Input Section */}
        <div className="section">
          <h2>{t.pipeFlowParameters}</h2>
          
          <div className="input-row">
            <div className="input-group">
              <label>{t.flowRate}</label>
              <input
                type="number"
                value={flowRate}
                onChange={(e) => setFlowRate(parseFloat(e.target.value) || 0)}
                step="0.1"
              />
            </div>
            <div className="select-group">
              <label>{t.flowUnit}</label>
              <select value={flowUnit} onChange={(e) => setFlowUnit(e.target.value)}>
                {Object.keys(flowUnitFactors).map(unit => (
                  <option key={unit} value={unit}>{t[unit] || unit}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="input-row">
            <div className="input-group">
              <label>{t.pipeDiameter}</label>
              <input
                type="number"
                value={diameter}
                onChange={(e) => setDiameter(parseFloat(e.target.value) || 0)}
                step="0.1"
              />
            </div>
            <div className="select-group">
              <label>{t.diameterUnit}</label>
              <select value={diameterUnit} onChange={(e) => setDiameterUnit(e.target.value)}>
                {Object.keys(diameterUnitFactors).map(unit => (
                  <option key={unit} value={unit}>{t[unit] || unit}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="input-row">
            <div className="input-group">
              <label>{t.pipeLength}</label>
              <input
                type="number"
                value={length}
                onChange={(e) => setLength(parseFloat(e.target.value) || 0)}
                step="0.1"
              />
            </div>
            <div className="select-group">
              <label>{t.lengthUnit}</label>
              <select value={lengthUnit} onChange={(e) => setLengthUnit(e.target.value)}>
                {Object.keys(lengthUnitFactors).map(unit => (
                  <option key={unit} value={unit}>{t[unit] || unit}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="full-width">
            <label>{t.pipeMaterial}</label>
            <select 
              value={material} 
              onChange={(e) => setMaterial(e.target.value)}
              className="material-select"
            >
              {Object.keys(materialOptions).map(mat => (
                <option key={mat} value={mat}>{t.materials[mat] || mat}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Elevation & Pressure Section */}
        <div className="section">
          <h2>{t.elevationPressure}</h2>
          
          <div className="input-row">
            <div className="input-group">
              <label>{t.suctionElevation}</label>
              <input
                type="number"
                value={suctionElevation}
                onChange={(e) => setSuctionElevation(parseFloat(e.target.value) || 0)}
                step="0.1"
              />
            </div>
            <div className="input-group">
              <label>{t.dischargeElevation}</label>
              <input
                type="number"
                value={dischargeElevation}
                onChange={(e) => setDischargeElevation(parseFloat(e.target.value) || 0)}
                step="0.1"
              />
            </div>
            <div className="select-group">
              <label>{t.elevationUnit}</label>
              <select value={elevationUnit} onChange={(e) => setElevationUnit(e.target.value)}>
                {Object.keys(lengthUnitFactors).map(unit => (
                  <option key={unit} value={unit}>{t[unit] || unit}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="input-row">
            <div className="input-group">
              <label>{t.suctionPressure}</label>
              <input
                type="number"
                value={suctionPressure}
                onChange={(e) => setSuctionPressure(parseFloat(e.target.value) || 0)}
                step="0.1"
              />
            </div>
            <div className="input-group">
              <label>{t.dischargePressure}</label>
              <input
                type="number"
                value={dischargePressure}
                onChange={(e) => setDischargePressure(parseFloat(e.target.value) || 0)}
                step="0.1"
              />
            </div>
            <div className="select-group">
              <label>{t.pressureUnit}</label>
              <select value={pressureUnit} onChange={(e) => setPressureUnit(e.target.value)}>
                {Object.keys(pressureUnitFactors).map(unit => (
                  <option key={unit} value={unit}>{t[unit] || unit}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Fittings Section */}
        <div className="section">
          <h2>{t.optionalFittings}</h2>
          
          <div className="fittings-grid">
            {Object.keys(fittings).map((fittingType) => (
              <div key={fittingType} className="fitting-input">
                <label>{t.fittings[fittingType]}</label>
                <input
                  type="number"
                  min="0"
                  value={fittings[fittingType]}
                  onChange={(e) => handleFittingChange(fittingType, e.target.value)}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Calculate Button */}
        <button className="calculate-btn" onClick={calculateHeadLoss}>
          {t.calculateButton}
        </button>

        {/* Error Display */}
        {error && (
          <div className="error">
            {error}
          </div>
        )}

        {/* Results Display */}
        {results && (
          <div className="results">
            <h3>{t.results}</h3>
            <div className="result-item">
              <strong>{t.flowVelocity}: </strong>
              <span className="blue-result">{formatVelocity(results.velocity)} {t.velocityUnit}</span>
            </div>
            <div className="result-item">
              <strong>{t.staticHead}: </strong>
              <span className="blue-result">{formatNumber(results.staticHead)} {t.headLossUnit}</span>
            </div>
            <div className="result-item">
              <strong>{t.frictionHead}: </strong>
              <span className="blue-result">{formatNumber(results.frictionHead)} {t.headLossUnit}</span>
            </div>
            <div className="result-item">
              <strong>{t.pressureHead}: </strong>
              <span className="blue-result">{formatNumber(results.pressureHead)} {t.headLossUnit}</span>
            </div>
            <div className="result-item">
              <strong>{t.velocityHead}: </strong>
              <span className="blue-result">{formatNumber(results.velocityHead)} {t.headLossUnit}</span>
            </div>
            <div className="result-item total-tdh">
              <strong>{t.totalDynamicHead}: </strong>
              <span className="blue-result">{formatNumber(results.totalDynamicHead)} {t.headLossUnit}</span>
            </div>
            <div className="result-item">
              <strong>{t.equivalentLength}: </strong>
              <span className="blue-result">{results.equivLength.toFixed(2)} {t.lengthResultUnit}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const App = () => {
  return (
    <LanguageProvider>
      <Calculator />
    </LanguageProvider>
  );
};

export default App;
